import type {
  Configuration,
  CreateMPAPolicyRequest,
  CreateMPARequestReviewRequest,
  ListMPARequestsRequest,
  ListMyMPARequestsRequest,
  MPAOperationType,
  MPARequest,
  UpdateMPAPolicyRequest,
} from '@repo/api-gw-sdk';
import { MpaApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/mpa';

export const mpa = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const mpaApi = new MpaApi(config);

  return {
    operations: {
      useList: () =>
        httpClient.execute(
          mpaApi.listMPAOperations.bind(mpaApi),
          baseUrl,
          'operations'
        ),
    },
    policies: {
      useList: (operation?: MPAOperationType) =>
        httpClient.execute(
          mpaApi.listMPAPolicies.bind(mpaApi),
          baseUrl,
          'policies',
          currentProjectId,
          operation
        ),
      create: (body: CreateMPAPolicyRequest) =>
        mpaApi.createMPAPolicy(currentProjectId, body),
      update: (mpaPolicyId: string, body: UpdateMPAPolicyRequest) =>
        mpaApi.updateMPAPolicy(currentProjectId, mpaPolicyId, body),
      delete: (mpaPolicyId: string) =>
        mpaApi.deleteMPAPolicy(currentProjectId, mpaPolicyId),
    },
    myRequests: {
      useList: (body?: ListMyMPARequestsRequest) =>
        httpClient.execute(
          mpaApi.listMyMPARequests.bind(mpaApi),
          baseUrl,
          'my-requests',
          currentProjectId,
          undefined,
          undefined,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          mpaApi.getMyMPARequest.bind(mpaApi),
          requestId ? baseUrl : null,
          `my-requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      // Replay the original API call captured on the MPA request, tagged with
      // the request id so the backend MPA middleware transitions it from
      // APPROVED to EXECUTED and lets the original handler run.
      apply: (request: MPARequest) => {
        if (!request.requestMethod || !request.requestPath) {
          throw new Error('MPA request is missing method or path');
        }
        const headers: Record<string, string> = {
          'X-MPA-Request-Id': request.id,
        };
        if (request.requestBody) {
          headers['Content-Type'] = 'application/json';
        }
        return httpClient.request(request.requestMethod, request.requestPath, {
          body: request.requestBody ?? undefined,
          headers,
        });
      },
    },
    requests: {
      useList: (body?: ListMPARequestsRequest) =>
        httpClient.execute(
          mpaApi.listMPARequests.bind(mpaApi),
          baseUrl,
          'requests',
          currentProjectId,
          undefined,
          undefined,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          mpaApi.getMPARequest.bind(mpaApi),
          requestId ? baseUrl : null,
          `requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      review: (requestId: string, body: CreateMPARequestReviewRequest) =>
        mpaApi.createMPARequestReview(currentProjectId, requestId, body),
    },
  };
};
